// Razorpay Service for handling payments (Frontend-only)
// Note: Server-side operations should be handled by a backend API

// Razorpay configuration
const RAZORPAY_KEY_ID = 'rzp_live_RCUomdLL0XJtB7';

export interface PaymentPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  features: string[];
  description: string;
}

// Credit conversion rate: ₹1 = 10 credits
export const CREDITS_PER_RUPEE = 10;

export interface CreditPackage {
  id: string;
  name: string;
  price: number; // Real money (INR)
  credits: number; // Base credits user receives
  bonusCredits?: number; // Bonus credits for larger packages
  currency: string;
  description: string;
  popular?: boolean; // Mark popular packages
  bestValue?: boolean; // Mark best value packages
  unlimited?: boolean; // If true, shows unlimited credits/minutes
  priceLabel?: string; // Custom price label (e.g., "per agent per channel")
}

export const CREDIT_PACKAGES: CreditPackage[] = [
  {
    id: 'starter',
    name: 'Starter Pack',
    price: 500,
    credits: 5000, // 500 * 10
    bonusCredits: 0,
    currency: 'INR',
    description: 'Perfect for trying out our services',
  },
  {
    id: 'popular',
    name: 'Popular Pack',
    price: 1999,
    credits: 19990, // 1999 * 10
    bonusCredits: 2010, // 10% bonus
    currency: 'INR',
    description: 'Most popular choice for regular users',
    popular: true,
  },
  {
    id: 'value',
    name: 'Value Pack',
    price: 4999,
    credits: 49990, // 4999 * 10
    bonusCredits: 10010, // 20% bonus
    currency: 'INR',
    description: 'Best value for growing businesses',
    bestValue: true,
  },
  {
    id: 'business',
    name: 'Business Pack',
    price: 10000,
    credits: 100000, // 10000 * 10
    bonusCredits: 30000, // 30% bonus
    currency: 'INR',
    description: 'For established businesses',
  },
  {
    id: 'unlimited',
    name: 'Unlimited Pack',
    price: 25000,
    credits: 0, // Not applicable for unlimited
    bonusCredits: 0, // Not applicable for unlimited
    currency: 'INR',
    description: 'Unlimited minutes for unlimited growth',
    unlimited: true,
    priceLabel: 'per agent per channel',
    popular: true, // Mark as popular to highlight
  }
];

// Helper function to convert real money to credits
export const convertMoneyToCredits = (amount: number): number => {
  return Math.round(amount * CREDITS_PER_RUPEE);
};

// Helper function to convert credits to real money (for display)
export const convertCreditsToMoney = (credits: number): number => {
  return credits / CREDITS_PER_RUPEE;
};

// Keep old PAYMENT_PLANS for backward compatibility if needed
export const PAYMENT_PLANS: PaymentPlan[] = [
  {
    id: 'mini',
    name: 'Mini Package',
    price: 500,
    currency: 'INR',
    features: ['Standard AI Agent', '66 calls per month', 'Email support', 'Basic analytics'],
    description: 'Great for small businesses'
  },
  {
    id: 'pro',
    name: 'Pro Plan',
    price: 1999,
    currency: 'INR',
    features: ['Advanced AI Agent', '266 calls per month', 'Priority support', 'Custom voice', 'Analytics'],
    description: 'For growing businesses'
  },
  {
    id: 'enterprise',
    name: 'Enterprise Plan',
    price: 4999,
    currency: 'INR',
    features: ['Premium AI Agent', '700 calls per month', '24/7 support', 'Custom integrations', 'API access'],
    description: 'For large organizations'
  },
  {
    id: 'business',
    name: 'Business Plan',
    price: 10000,
    currency: 'INR',
    features: ['Premium AI Agent', '1,340 calls per month', '24/7 support', 'Custom integrations', 'API access', 'Dedicated account manager'],
    description: 'For established businesses'
  },
  {
    id: 'corporate',
    name: 'Corporate Plan',
    price: 50000,
    currency: 'INR',
    features: ['Premium AI Agent', '6,700 calls per month', '24/7 support', 'Custom integrations', 'API access', 'Dedicated account manager', 'Custom SLA'],
    description: 'For enterprise corporations'
  }
];

// Frontend-only Razorpay service
// All server-side operations should be handled by your backend API